import { create } from 'zustand';
import { message } from 'antd';
import {
    getFlowEnrollmentBoard,
    getContactEnrollments,
    updateFlowEnrollment,
} from '../reducers/flow-enrollment/resources';

const STALL_THRESHOLD_DAYS = 3;

const useFlowEnrollmentStore = create((set, get) => ({
    // Board state keyed by modulegroup_id
    boardData: {},       // { [modulegroupId]: enrollment[] }
    boardLoading: {},    // { [modulegroupId]: boolean }

    // Contact enrollments
    contactEnrollments: [],
    contactEnrollmentsLoading: false,

    // Panel state
    selectedEnrollment: null,

    fetchBoard: async (modulegroupId, campaignId) => {
        const key = campaignId ? `${modulegroupId}:${campaignId}` : modulegroupId;
        set((s) => ({ boardLoading: { ...s.boardLoading, [key]: true } }));
        try {
            const res = await getFlowEnrollmentBoard(modulegroupId, campaignId);
            const enrollments = res.data?.data || res.data || [];
            set((s) => ({
                boardData: { ...s.boardData, [key]: enrollments },
                boardLoading: { ...s.boardLoading, [key]: false },
            }));
        } catch (error) {
            console.error('Failed to fetch board:', error);
            set((s) => ({ boardLoading: { ...s.boardLoading, [key]: false } }));
            message.error('Failed to load enrollment board');
        }
    },

    fetchContactEnrollments: async (contactId) => {
        set({ contactEnrollmentsLoading: true });
        try {
            const res = await getContactEnrollments(contactId);
            set({
                contactEnrollments: res.data?.data || res.data || [],
                contactEnrollmentsLoading: false,
            });
        } catch (error) {
            console.error('Failed to fetch contact enrollments:', error);
            set({ contactEnrollmentsLoading: false });
        }
    },

    moveEnrollment: async (enrollmentId, modulegroupId, current_module_id, campaignId) => {
        const key = campaignId ? `${modulegroupId}:${campaignId}` : modulegroupId;
        // Optimistic update
        set((s) => {
            const prev = s.boardData[key] || [];
            return {
                boardData: {
                    ...s.boardData,
                    [key]: prev.map((e) =>
                        e.id === enrollmentId ? { ...e, current_module_id } : e
                    ),
                },
            };
        });
        try {
            await updateFlowEnrollment(enrollmentId, { current_module_id });
            message.success('Enrollment moved');
            set({ selectedEnrollment: null });
        } catch (error) {
            console.error(error);
            message.error('Failed to move enrollment — refreshing');
            set({ selectedEnrollment: null });
            get().fetchBoard(modulegroupId, campaignId);
        }
    },

    dropEnrollment: async (enrollmentId, modulegroupId, campaignId, dropReason) => {
        const key = campaignId ? `${modulegroupId}:${campaignId}` : modulegroupId;
        // Optimistic update
        set((s) => {
            const prev = s.boardData[key] || [];
            return {
                boardData: {
                    ...s.boardData,
                    [key]: prev.map((e) =>
                        e.id === enrollmentId ? { ...e, status: 'dropped', drop_reason: dropReason || null } : e
                    ),
                },
            };
        });
        try {
            const payload = { status: 'dropped' };
            if (dropReason) payload.drop_reason = dropReason;
            await updateFlowEnrollment(enrollmentId, payload);
            message.success('Enrollment dropped');
            set({ selectedEnrollment: null });
        } catch (error) {
            console.error(error);
            message.error('Failed to drop enrollment — refreshing');
            get().fetchBoard(modulegroupId, campaignId);
        }
    },

    setSelectedEnrollment: (enrollment) => set({ selectedEnrollment: enrollment }),

    isStalled: (enrollment) => {
        if (enrollment.status !== 'active') return false;
        const updatedAt = new Date(enrollment.updated_at);
        const daysSince = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
        return daysSince > STALL_THRESHOLD_DAYS;
    },
}));

export default useFlowEnrollmentStore;
